/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useSidebarState.ts
 🗓️ Created: 2025-01-05
 🧩 Description:
    Custom hook for detecting sidebar state and obtaining its real width
    with responsive behavior and dynamic width calculation.

 🔧 Notes:
    - Implements sidebar state detection with ResizeObserver
    - Provides real-time width calculation and open/closed state
    - Supports responsive sidebar behavior
    - Handles window resize events and sidebar state changes
    - Provides accurate width measurements for layout calculations
    - Integrates with theme store for dynamic sidebar management

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect } from 'react';

/**
 * Hook para detectar el estado del sidebar y obtener su ancho real
 * @returns {object} { isOpen: boolean, width: number }
 */
export const useSidebarState = (): { isOpen: boolean; width: number } => {
  const [sidebarState, setSidebarState] = useState({ isOpen: true, width: 0 });

  useEffect(() => {
    // Detectar el estado del sidebar por la clase CSS o atributo data
    const checkSidebarState = () => {
      // Intentar múltiples selectores para encontrar el sidebar
      const selectors = [
        '[data-sidebar]',
        '.sidebar',
        '[role="navigation"]',
        'nav',
        'aside',
        '.fixed.left-0',
        '.fixed.inset-y-0'
      ];

      let sidebar = null;
      for (const selector of selectors) {
        sidebar = document.querySelector(selector);
        if (sidebar) break;
      }

      if (sidebar) {
        const rect = sidebar.getBoundingClientRect();
        const isVisible = rect.width > 0 && !sidebar.classList.contains('hidden');

        // Use CSS variable value for consistent positioning with fixed elements
        // The measured width can differ slightly due to scrollbars/borders
        const sidebarCssWidth = getComputedStyle(document.documentElement).getPropertyValue('--sidebar-width').trim();
        const collapsedCssWidth = getComputedStyle(document.documentElement).getPropertyValue('--sidebar-collapsed-width').trim();

        // Parse the CSS variable values (e.g., "350px" -> 350)
        const expandedWidth = sidebarCssWidth ? parseInt(sidebarCssWidth) : 350;
        const collapsedWidth = collapsedCssWidth ? parseInt(collapsedCssWidth) : 80;

        // Determine if collapsed based on measured width being closer to collapsed value
        const isCollapsed = rect.width > 0 && rect.width < (expandedWidth + collapsedWidth) / 2;
        const width = isCollapsed ? collapsedWidth : expandedWidth;

        // Solo actualizar si el sidebar está completamente renderizado (rect.width > 0)
        if (rect.width > 0) {
          setSidebarState(prevState => {
            if (prevState.isOpen !== isVisible || prevState.width !== width) {
              return { isOpen: isVisible, width };
            }
            return prevState;
          });
        }
      } else {
        // Fallback: asumir que está abierto por defecto solo si no tenemos estado previo
        setSidebarState(prevState => {
          if (prevState.width === 0) {
            return { isOpen: true, width: 256 };
          }
          return prevState;
        });
      }
    };

    // Verificar estado inicial con un delay para asegurar que el DOM esté completamente renderizado
    const timeoutId = setTimeout(checkSidebarState, 150);

    // Verificación adicional después de que la página esté completamente cargada
    const loadTimeoutId = setTimeout(checkSidebarState, 500);

    // Crear un observer para detectar cambios en el sidebar
    const observer = new MutationObserver(() => {
      // Usar requestAnimationFrame para asegurar que los cambios del DOM se hayan aplicado
      requestAnimationFrame(checkSidebarState);
    });

    // Observar cambios en el body para detectar cambios de clase
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class']
    });

    // También escuchar cambios de tamaño de ventana
    window.addEventListener('resize', () => {
      requestAnimationFrame(checkSidebarState);
    });

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(loadTimeoutId);
      observer.disconnect();
      window.removeEventListener('resize', checkSidebarState);
    };
  }, []);

  return sidebarState;
};

export default useSidebarState;
